import { CheckCircleOutlined, ClockCircleOutlined, WarningOutlined } from "@ant-design/icons";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import React from "react";
import { formatNumberByCountry } from "../../utils/formatNumbers";
import { DECIMALES_EN_INICIO } from "../../utils/decimalesDelImporte";
import EstadoDeLosDocumentos from "./EstadoDeLosDocumentos";
import { TituloConAyuda } from "../Layout/AyudaDePantalla";

const { RangePicker } = DatePicker;

const PALETTE = ["#52c41a", "#1677ff", "#722ed1", "#fa8c16", "#00828b", "#f5222d"];

// Monto monetario: separador de miles, símbolo y cantidad de decimales según
// el país (ej. Paraguay usa 0 decimales para el guaraní).
// El panel de Inicio escribe con DOS decimales, no con los del pais.
//
// Es un tablero de un vistazo, no un documento: en Costa Rica, que escribe
// con cinco, una cifra de ventas del dia saldria «12.345.678,90000» y la
// magnitud --que es lo unico que se mira aqui-- se pierde entre ceros.
const money = (value, country) =>
  formatNumberByCountry(value || 0, country, true, DECIMALES_EN_INICIO);

// Conteo entero (sin moneda) con separador de miles del país.
const count = (value, country) =>
  country ? formatNumberByCountry(value || 0, country, false, 0) : String(value ?? 0);

const formatRelative = (m) => {
  if (!m || !m.isValid()) return "-";
  const now = dayjs();
  if (m.isSame(now, "day")) return `Hoy ${m.format("HH:mm")}`;
  if (m.isSame(now.subtract(1, "day"), "day")) return `Ayer ${m.format("HH:mm")}`;
  return m.format("DD/MM/YYYY HH:mm");
};

const RANGE_OPTIONS = [
  { key: "today", label: "Hoy" },
  { key: "week", label: "Semana" },
  { key: "month", label: "Mes" },
];

const DashboardMetrics = ({
  metrics,
  shiftInfo,
  country,
  range,
  onRangeChange,
  customRange,
  onCustomRangeChange,
}) => {
  if (!metrics) return null;

  const {
    ventasNetas,
    vendidoAmount,
    devueltoAmount,
    ticketPromedio,
    facturasCount,
    notasCreditoCount,
    ventasPorFormaDePago,
    ventasPorCajaTurno,
    productosMasVendidos,
    clientesTotal,
    clientesNuevos,
    mejoresClientes,
    cuponesYCampanas,
    cuponesUsosTotal,
    cuponesDescuentoTotal,
    facturasConError,
    cobradoNeto,
    diferenciaConVentasNetas,
    causaDeLaDiferencia,
    estadoDeLosDocumentos,
    sinEnviar,
  } = metrics;

  const maxCajaAmount = Math.max(1, ...ventasPorCajaTurno.map((g) => g.amount));
  const maxProductQty = Math.max(1, ...productosMasVendidos.map((p) => p.quantity));

  let donutOffset = 0;
  const donutStops = ventasPorFormaDePago.map((group, index) => {
    const start = donutOffset;
    donutOffset += group.percent;
    return `${PALETTE[index % PALETTE.length]} ${start}% ${donutOffset}%`;
  });
  const donutBackground = donutStops.length
    ? `conic-gradient(${donutStops.join(", ")})`
    : "var(--border-soft, #f0f1f3)";

  const ultimaApertura = shiftInfo?.ultimaApertura;
  const ultimoCierre = shiftInfo?.ultimoCierre;
  const aperturaMoment = ultimaApertura
    ? dayjs(`${ultimaApertura.FechaDeApertura}T${ultimaApertura.HoraDeApertura || "00:00"}`)
    : null;
  const cierreMoment = ultimoCierre ? dayjs(ultimoCierre.lastModified) : null;
  const cierreEsPosteriorAApertura =
    cierreMoment && aperturaMoment ? cierreMoment.isAfter(aperturaMoment) : false;

  return (
    <div className="dash-metrics">
      <div className="page-head">
        <div>
          <h2 className="section-title">
            {/* Qué significa cada cifra del tablero: config/ayuda/ayudasDePantalla.js */}
            <TituloConAyuda titulo="Resumen de actividad" ayuda="inicio" />
          </h2>
          <p className="page-head__sub">Métricas calculadas sobre el período seleccionado</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div className="range">
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                className={range === opt.key ? "is-active" : ""}
                onClick={() => onRangeChange(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <RangePicker
            size="middle"
            format="DD/MM/YYYY"
            allowClear={false}
            value={customRange}
            onChange={(dates) => {
              if (!dates) return;
              onCustomRangeChange(dates);
              onRangeChange("custom");
            }}
          />
        </div>
      </div>

      {/* KPI row */}
      <div className="kpi-row">
        <div className="kpi" style={{ "--bar-color": "#00828b" }}>
          <div className="kpi__label">Ventas netas</div>
          <div className="kpi__value">{money(ventasNetas, country)}</div>
          <div className="kpi__meta">
            {count(facturasCount, country)} factura{facturasCount === 1 ? "" : "s"} −{" "}
            {count(notasCreditoCount, country)} nota
            {notasCreditoCount === 1 ? "" : "s"} de crédito
          </div>
        </div>
        <div className="kpi" style={{ "--bar-color": "#1677ff" }}>
          <div className="kpi__label">Ticket promedio</div>
          <div className="kpi__value">{money(ticketPromedio, country)}</div>
          <div className="kpi__meta">
            sobre {count(facturasCount, country)} factura{facturasCount === 1 ? "" : "s"} emitida
            {facturasCount === 1 ? "" : "s"}
          </div>
        </div>
        <div className="kpi" style={{ "--bar-color": "#52c41a" }}>
          <div className="kpi__label">Facturas vs. notas de crédito</div>
          <div className="kpi__value">
            {count(facturasCount, country)} <small>/ {count(notasCreditoCount, country)}</small>
          </div>
          <div className="kpi__meta">
            {money(vendidoAmount, country)} vendido · {money(devueltoAmount, country)} devuelto
          </div>
        </div>
        <div className={`kpi ${facturasConError > 0 ? "kpi--warning" : ""}`}>
          <div className="kpi__label">Errores de sincronización</div>
          <div className="kpi__value">{count(facturasConError, country)}</div>
          <div className="kpi__meta">
            {facturasConError > 0 ? (
              <>
                <span className="dot dot--danger"></span>
                documento{facturasConError === 1 ? "" : "s"} sin poder enviarse a BC
              </>
            ) : (
              "todo sincronizado correctamente"
            )}
          </div>
        </div>
      </div>

      {/* Main grid */}
      <div className="grid">
        <div className="col">
          {/* Formas de pago */}
          <div className="panel">
            <div className="panel__head">
              <h3 className="panel__title">Ventas por forma de pago</h3>
            </div>
            <p className="panel__sub">Lo cobrado en facturas menos lo devuelto en notas de crédito</p>
            {ventasPorFormaDePago.length === 0 ? (
              <p className="panel__empty">Sin ventas registradas en el período.</p>
            ) : (
              <>
              {/*
                El total va encima de la gráfica y no dentro del círculo: el hueco
                mide 78 px y un importe de once cifras en colones no cabía, se
                salía del anillo y tapaba el color de la primera forma de pago.
              */}
              <div className="donut-total" role="group" aria-label="Total neto">
                {/*
                  A la izquierda, por qué no cuadra con Ventas netas: la cifra y los
                  documentos que la explican. A la derecha, el total con su etiqueta
                  encima. Neto, como el arqueo: cuando cuadra, el renglón va vacío.
                */}
                <span className="donut-total__cause">
                  {diferenciaConVentasNetas
                    ? `Difiere de Ventas netas en ${money(Math.abs(diferenciaConVentasNetas), country)}${
                        causaDeLaDiferencia ? ` · ${causaDeLaDiferencia}` : ""
                      }`
                    : ""}
                </span>
                <span className="donut-total__amount">
                  <span className="donut-total__label">Total neto</span>
                  <b className="donut-total__value">{money(cobradoNeto, country)}</b>
                </span>
              </div>
              <div className="donut-row">
                <div
                  className="donut"
                  role="img"
                  aria-label="Distribución del monto por forma de pago"
                  style={{ background: donutBackground }}
                >
                  <div className="donut__hole"></div>
                </div>
                <div className="legend">
                  {ventasPorFormaDePago.map((group, index) => (
                    <div className="legend__row" key={group.label}>
                      <span className="legend__key">
                        <span
                          className="swatch"
                          style={{ background: PALETTE[index % PALETTE.length] }}
                        ></span>
                        {group.label}
                      </span>
                      <span className="legend__value">
                        {group.percent.toFixed(0)}% · {money(group.amount, country)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              </>
            )}
          </div>

          {/* Ventas por caja/turno */}
          <div className="panel">
            <div className="panel__head">
              <h3 className="panel__title">Ventas por caja y turno</h3>
            </div>
            <p className="panel__sub">Todas las cajas activas en el período</p>
            {ventasPorCajaTurno.length === 0 ? (
              <p className="panel__empty">Sin ventas registradas en el período.</p>
            ) : (
              <div className="bar-list">
                {ventasPorCajaTurno.map((group, index) => (
                  <div key={`${group.cajaLabel}-${group.turnoLabel}`}>
                    <div className="bar-item__top">
                      <span className="bar-item__name">
                        {group.cajaLabel} · Turno {group.turnoLabel}
                        {index === 0 && <span className="tag">Mejor caja</span>}
                      </span>
                      <span className="bar-item__value">
                        {money(group.amount, country)} · {count(group.count, country)} venta
                        {group.count === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className="bar-track">
                      <div
                        className="bar-fill"
                        style={{
                          width: `${(group.amount / maxCajaAmount) * 100}%`,
                          "--fill": PALETTE[index % PALETTE.length],
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Productos más vendidos */}
          <div className="panel">
            <div className="panel__head">
              <h3 className="panel__title">Productos más vendidos</h3>
            </div>
            <p className="panel__sub">Por unidades vendidas en el período</p>
            {productosMasVendidos.length === 0 ? (
              <p className="panel__empty">Sin ventas registradas en el período.</p>
            ) : (
              <div className="bar-list">
                {productosMasVendidos.map((product) => (
                  <div key={product.label}>
                    <div className="bar-item__top">
                      <span className="bar-item__name">{product.label}</span>
                      <span className="bar-item__value">
                        {count(product.quantity, country)} u · {money(product.amount, country)}
                      </span>
                    </div>
                    <div className="bar-track">
                      <div
                        className="bar-fill"
                        style={{ width: `${(product.quantity / maxProductQty) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="col">
          {/* Clientes */}
          <div className="panel">
            <div className="panel__head">
              <h3 className="panel__title">Clientes</h3>
            </div>
            <p className="panel__sub">Registrados en el sistema</p>
            <div className="stat-pair">
              <div>
                <div className="n">{count(clientesTotal, country)}</div>
                <div className="l">Total registrados</div>
              </div>
              <div>
                <div className="n">{count(clientesNuevos, country)}</div>
                <div className="l">Nuevos en el período</div>
              </div>
            </div>
          </div>

          {/* Mejores clientes */}
          <div className="panel">
            <div className="panel__head">
              <h3 className="panel__title">Mejores clientes</h3>
            </div>
            <p className="panel__sub">Por monto acumulado de compra en el período</p>
            {mejoresClientes.length === 0 ? (
              <p className="panel__empty">Sin ventas registradas en el período.</p>
            ) : (
              <table className="mini">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {mejoresClientes.map((client, index) => (
                    <tr key={client.label}>
                      <td>
                        <span className="client-rank">{index + 1}</span>
                        {client.label}
                      </td>
                      <td className="client-amount">{money(client.amount, country)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Cupones y campañas */}
          <div className="panel">
            <div className="panel__head">
              <h3 className="panel__title">Cupones y campañas</h3>
            </div>
            <p className="panel__sub">Uso en el período</p>
            {cuponesYCampanas.length === 0 ? (
              <p className="panel__empty">No se aplicaron cupones ni campañas.</p>
            ) : (
              <>
                <div className="coupon-summary">
                  <b>
                    {count(cuponesUsosTotal, country)} venta{cuponesUsosTotal === 1 ? "" : "s"}
                  </b>
                  <span>{money(cuponesDescuentoTotal, country)} en descuentos otorgados</span>
                </div>
                {cuponesYCampanas.map((group) => (
                  <div className="coupon-row" key={group.code}>
                    <span className="coupon-row__name">
                      <span className="coupon-code">{group.label}</span>
                      {count(group.count, country)} uso{group.count === 1 ? "" : "s"}
                    </span>
                    <span className="coupon-row__value">{money(group.amount, country)}</span>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Última apertura/cierre */}
          <div className="panel">
            <div className="panel__head">
              <h3 className="panel__title">Apertura y cierre</h3>
            </div>
            <p className="panel__sub">{shiftInfo?.cajaLabel || "Caja"}</p>
            <div className="shift-row">
              <div className="shift-icon shift-icon--open">
                <CheckCircleOutlined />
              </div>
              <div>
                <div className="shift-title">
                  {ultimaApertura ? "Apertura registrada" : "Sin apertura registrada"}
                </div>
                <div className="shift-meta">
                  {ultimaApertura
                    ? `${formatRelative(aperturaMoment)} · Cajero ${ultimaApertura.Cajero || "-"} · ${money(ultimaApertura.ImporteDeApertura, country)}`
                    : "-"}
                </div>
              </div>
            </div>
            <div className="shift-row">
              <div className="shift-icon shift-icon--close">
                {ultimoCierre && cierreEsPosteriorAApertura ? (
                  <CheckCircleOutlined />
                ) : ultimaApertura?.Estado === "Abierto" ? (
                  <ClockCircleOutlined />
                ) : (
                  <WarningOutlined />
                )}
              </div>
              <div>
                <div
                  className="shift-title"
                  style={
                    ultimoCierre && cierreEsPosteriorAApertura
                      ? undefined
                      : { color: "var(--text-tertiary, rgba(0,0,0,0.4))" }
                  }
                >
                  {ultimoCierre && cierreEsPosteriorAApertura
                    ? "Cierre registrado"
                    : "Sin cierre registrado"}
                </div>
                <div className="shift-meta">
                  {ultimoCierre
                    ? `Último cierre: ${formatRelative(cierreMoment)}`
                    : "Aún no se ha registrado ningún cierre"}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col col--estado">
          <EstadoDeLosDocumentos
            estado={estadoDeLosDocumentos}
            sinEnviar={sinEnviar}
            country={country}
          />
        </div>
      </div>
    </div>
  );
};

export default DashboardMetrics;
